import { useGetAllReportsQuery } from '../api/reportsApiSlice';
import { useGetTapahtumatByIdsQuery } from '../api/eventsApiSlice';
import useDocumentTitle from '../hooks/useDocumentTitle';
import { useState, useEffect } from 'react';
import { Typography, Grid, Paper, Box, IconButton, TextField, Button, CircularProgress, Stack, Chip, MenuItem } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ProgressLoader from '../components/ProgressLoader';
import ReportsModal from '../utils/ReportsModal';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import writeExcelFile from 'write-excel-file/browser';

dayjs.extend(customParseFormat);

const EXPORT_COLUMNS = [
    'Tapahtuma ID',
    'Tapahtuman nimi',
    'Osoite',
    'Kunta',
    'Päivämäärä',
    'Yhteistyötaho',
    'Suhtautuminen',
    'Muistiinpano',
    'Väkimäärä'
];

// Utility function to strip HTML tags
const stripHtmlTags = (str) => {
    if (!str) return '';
    return str.replace(/<[^>]+>/g, '');
};

// Utility function to estimate column width based on content
const getColumnWidth = (data, key) => {
    const maxLength = Math.max(
        key.length, // Header length
        ...data.map(row => (row[key] ? String(row[key]).length : 0)) // Content length
    );
    return Math.min(maxLength * 1.2, 50); // Scale and cap width
};

// Utility function to estimate row height based on content
const getRowHeight = (text) => {
    if (!text) return 20;
    const lineCount = String(text).split('\n').length;
    return Math.max(20, lineCount * 15); // Base height or scaled by lines
};

const waitForPaint = () => new Promise((resolve) => {
    requestAnimationFrame(() => resolve());
});

const parseEventDate = (value) => {
    if (!value) {
        return null;
    }

    if (dayjs.isDayjs(value)) {
        return value;
    }

    if (typeof value === 'string') {
        const normalizedValue = value.trim();
        const localizedDateTime = dayjs(normalizedValue, 'DD.MM.YYYY HH:mm', true);
        if (localizedDateTime.isValid()) {
            return localizedDateTime;
        }

        const localizedDate = dayjs(normalizedValue, 'DD.MM.YYYY', true);
        if (localizedDate.isValid()) {
            return localizedDate;
        }
    }

    const parsed = dayjs(value);
    return parsed.isValid() ? parsed : null;
};

const getDateValue = (value) => {
    const parsed = parseEventDate(value);
    return parsed ? parsed.valueOf() : 0;
};

const formatDateLabel = (value) => {
    const parsed = parseEventDate(value);
    if (!parsed) {
        return 'Ajankohta puuttuu';
    }

    return parsed.format('DD.MM.YYYY HH:mm');
};

const getSuhtautuminenMeta = (value) => {
    if (value === null || value === undefined || Number.isNaN(value)) {
        return {
            label: 'Ei arviota',
            color: '#546e7a',
            backgroundColor: '#eceff1'
        };
    }

    if (value >= 0.5) {
        return {
            label: 'Myönteinen',
            color: '#2e7d32',
            backgroundColor: '#e8f5e9'
        };
    }

    if (value >= 0) {
        return {
            label: 'Neutraali',
            color: '#ed6c02',
            backgroundColor: '#fff3e0'
        };
    }

    return {
        label: 'Kielteinen',
        color: '#d32f2f',
        backgroundColor: '#ffebee'
    };
};

const SORT_OPTIONS = [
    { value: 'reports-desc', label: 'Eniten raportteja' },
    { value: 'latest-desc', label: 'Tuoreimmat ensin' },
    { value: 'sentiment-desc', label: 'Myönteisin ensin' },
    { value: 'alphabetical', label: 'Aakkosjärjestys' }
];

const buildExportRows = (reports, tapahtumat) => {
    if (!reports?.length) {
        return [];
    }

    const data = [];

    reports.forEach(report => {
        const tapahtuma = tapahtumat?.find(t => {
            const match = t.id === report.tapahtumaId;
            return match;
        });
        const tapahtumaNimi = tapahtuma?.nimi || 'N/A';
        const osoite = tapahtuma?.paikka?.osoite || 'N/A';
        const kunta = tapahtuma?.paikka?.kunta || 'N/A';
        const pvm = tapahtuma?.aloitus ? formatDateLabel(tapahtuma.aloitus) : 'N/A';
        const muistiinpano = stripHtmlTags(report.muistiinpano || '');
        const vakimaara = report.vakimaara || 'N/A';

        report.yhteistyotahot.forEach(({ nimi, suhtautuminen }) => {
            data.push({
                'Tapahtuma ID': report.tapahtumaId,
                'Tapahtuman nimi': tapahtumaNimi,
                'Osoite': osoite,
                'Kunta': kunta,
                'Päivämäärä': pvm,
                'Yhteistyötaho': nimi,
                'Suhtautuminen': suhtautuminen ?? 'N/A',
                'Muistiinpano': muistiinpano,
                'Väkimäärä': vakimaara
            });
        });
    });

    data.sort((a, b) => {
        const dateA = a['Päivämäärä'] === 'N/A' ? 0 : dayjs(a['Päivämäärä'], 'DD.MM.YYYY HH:mm').valueOf();
        const dateB = b['Päivämäärä'] === 'N/A' ? 0 : dayjs(b['Päivämäärä'], 'DD.MM.YYYY HH:mm').valueOf();
        return dateB - dateA;
    });

    return data;
};

const StatisticsPage = ({ documentTitle }) => {
    useDocumentTitle(documentTitle);
    const { data: reports, isLoading: isReportsLoading } = useGetAllReportsQuery();
    const ids = reports ? [...new Set(reports.map(report => report.tapahtumaId))] : [];
    const { data: tapahtumat, isLoading: isTapahtumatLoading } = useGetTapahtumatByIdsQuery(
        ids,
        { skip: !reports }
    );
    const itemsPerPage = 10;
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(0);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedYhteistyotaho, setSelectedYhteistyotaho] = useState('');
    const [exportType, setExportType] = useState(null);
    const [sortBy, setSortBy] = useState('reports-desc');

    const tapahtumatMap = (tapahtumat || []).reduce((acc, tapahtuma) => {
        acc[tapahtuma.id] = tapahtuma;
        return acc;
    }, {});

    const yhteistyotahotData = reports?.reduce((acc, item) => {
        const yhteistyotahot = item.yhteistyotahot;
        if (!yhteistyotahot || yhteistyotahot.length === 0) return acc;

        const tapahtuma = tapahtumatMap[item.tapahtumaId];
        const reportDateValue = getDateValue(tapahtuma?.aloitus);
        const reportDateLabel = formatDateLabel(tapahtuma?.aloitus);
        const muistiinpano = stripHtmlTags(item.muistiinpano || '').trim() || 'Ei muistiinpanoja';
        const osoite = tapahtuma?.paikka?.osoite || 'Osoite puuttuu';
        const kunta = tapahtuma?.paikka?.kunta || '';
        const tapahtumaNimi = tapahtuma?.nimi || 'Nimetön tapahtuma';

        yhteistyotahot.forEach(({ nimi, suhtautuminen }) => {
            if (!nimi) return;

            if (!acc[nimi]) {
                acc[nimi] = {
                    name: nimi,
                    suhtautuminenSum: 0,
                    arvioitujenRaporttienMaara: 0,
                    reportCount: 0,
                    latestReportDateValue: 0,
                    latestReportLabel: 'Ajankohta puuttuu',
                    latestEventName: 'Ei tapahtumaa',
                    latestLocation: 'Sijainti puuttuu',
                    reports: []
                };
            }

            acc[nimi].reportCount++;

            if (suhtautuminen !== undefined && suhtautuminen !== null) {
                acc[nimi].suhtautuminenSum += suhtautuminen;
                acc[nimi].arvioitujenRaporttienMaara++;
            }

            acc[nimi].reports.push({
                id: item.id,
                tapahtumaId: item.tapahtumaId,
                tapahtumaNimi,
                paivamaara: reportDateLabel,
                paivamaaraValue: reportDateValue,
                osoite,
                kunta,
                vakimaara: item.vakimaara ?? 'Ei ilmoitettu',
                muistiinpano,
                suhtautuminen
            });

            if (reportDateValue >= acc[nimi].latestReportDateValue) {
                acc[nimi].latestReportDateValue = reportDateValue;
                acc[nimi].latestReportLabel = reportDateLabel;
                acc[nimi].latestEventName = tapahtumaNimi;
                acc[nimi].latestLocation = kunta ? `${osoite}, ${kunta}` : osoite;
            }
        });

        return acc;
    }, {});

    const yhteistyotahot = Object.values(yhteistyotahotData || {}).map((item) => {
        const averageSuhtautuminen = item.arvioitujenRaporttienMaara
            ? item.suhtautuminenSum / item.arvioitujenRaporttienMaara
            : null;

        return {
            ...item,
            averageSuhtautuminen,
            averageMeta: getSuhtautuminenMeta(averageSuhtautuminen),
            reports: [...item.reports].sort((a, b) => b.paivamaaraValue - a.paivamaaraValue)
        };
    });

    const sortedYhteistyotahot = [...yhteistyotahot].sort((a, b) => {
        if (sortBy === 'latest-desc') {
            return b.latestReportDateValue - a.latestReportDateValue || a.name.localeCompare(b.name, 'fi');
        }

        if (sortBy === 'sentiment-desc') {
            return (b.averageSuhtautuminen ?? -2) - (a.averageSuhtautuminen ?? -2) || b.reportCount - a.reportCount;
        }

        if (sortBy === 'alphabetical') {
            return a.name.localeCompare(b.name, 'fi');
        }

        return b.reportCount - a.reportCount || b.latestReportDateValue - a.latestReportDateValue;
    });

    const filteredYhteistyotahot = searchTerm
        ? sortedYhteistyotahot.filter(({ name }) =>
            name.toLowerCase().includes(searchTerm.toLowerCase())
        )
        : sortedYhteistyotahot;

    const totalPages = Math.max(1, Math.ceil(filteredYhteistyotahot?.length / itemsPerPage));

    useEffect(() => {
        if (currentPage >= totalPages) {
            setCurrentPage(totalPages - 1);
        }
    }, [searchTerm, totalPages]);

    const startIndex = currentPage * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const displayedYhteistyotahot = filteredYhteistyotahot?.slice(startIndex, endIndex);
    const selectedYhteistyotahoData = yhteistyotahot.find(({ name }) => name === selectedYhteistyotaho);
    const totalReportCount = yhteistyotahot.reduce((sum, item) => sum + item.reportCount, 0);
    const visibleReportCount = filteredYhteistyotahot?.reduce((sum, item) => sum + item.reportCount, 0) || 0;
    const topYhteistyotaho = yhteistyotahot.length
        ? [...yhteistyotahot].sort((a, b) => b.reportCount - a.reportCount || a.name.localeCompare(b.name, 'fi'))[0]
        : null;
    const bestAverage = yhteistyotahot
        .filter(({ averageSuhtautuminen }) => averageSuhtautuminen !== null)
        .sort((a, b) => b.averageSuhtautuminen - a.averageSuhtautuminen)[0];

    const handleNextPage = () => {
        if (currentPage < totalPages - 1) {
            setCurrentPage(currentPage + 1);
        }
    };

    const handlePreviousPage = () => {
        if (currentPage > 0) {
            setCurrentPage(currentPage - 1);
        }
    };

    const handleModalOpen = (yhteistyotaho) => {
        setSelectedYhteistyotaho(yhteistyotaho);
        setModalOpen(true);
    };

    const handleModalClose = () => {
        setModalOpen(false);
        setSelectedYhteistyotaho('');
    };

    const exportToCSV = async () => {
        try {
            setExportType('csv');
            await waitForPaint();

            const data = buildExportRows(reports, tapahtumat);
            const csvRows = [EXPORT_COLUMNS];
            data.forEach(row => {
                csvRows.push([
                    row['Tapahtuma ID'],
                    row['Tapahtuman nimi'],
                    row['Osoite'],
                    row['Kunta'],
                    row['Päivämäärä'],
                    row['Yhteistyötaho'],
                    row['Suhtautuminen'],
                    row['Muistiinpano'],
                    row['Väkimäärä']
                ]);
            });

            const csvContent = '\uFEFF' + csvRows.map(row => row.join(',')).join('\n');
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.setAttribute('href', url);
            link.setAttribute('download', 'raportitData.csv');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } finally {
            setExportType(null);
        }
    };

    const exportToExcel = async () => {
        try {
            setExportType('excel');
            await waitForPaint();

            const data = buildExportRows(reports, tapahtumat);
            const columns = EXPORT_COLUMNS.map(key => ({ width: getColumnWidth(data, key) }));
            const headerRow = EXPORT_COLUMNS.map(column => ({
                value: column,
                fontWeight: 'bold',
                backgroundColor: '#f5f5f5',
                align: 'left',
                alignVertical: 'center',
                height: 24
            }));
            const sheetData = [
                headerRow,
                ...data.map(row => {
                    const rowHeight = getRowHeight(row['Muistiinpano']);

                    return EXPORT_COLUMNS.map(column => ({
                        value: row[column],
                        wrap: column === 'Muistiinpano',
                        alignVertical: 'top',
                        height: rowHeight
                    }));
                })
            ];

            await writeExcelFile(sheetData, { columns }).toFile('raportitData.xlsx');
        } catch (error) {
            console.error('Excel export failed:', error);
        } finally {
            setExportType(null);
        }
    };

    if (isReportsLoading || isTapahtumatLoading) {
        return <ProgressLoader msg="Ladataan tilastoja.." />;
    }

    return (
        <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, bgcolor: '#fcfcfd' }}>
            <Stack spacing={3}>
                <Box
                    sx={{
                        p: { xs: 2, md: 3 },
                        borderRadius: 3,
                        background: 'linear-gradient(135deg, #f5f7fa 0%, #eef4ff 100%)',
                        border: '1px solid #e2e8f0'
                    }}
                >
                    <Stack spacing={1.5}>
                        <Typography variant="h4" sx={{ fontFamily: 'Arial', fontWeight: 700 }}>
                            Yhteistyötahojen tilastot
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Näet nopeasti aktiivisimmat yhteistyötahot, viimeisimmät kontaktit ja yleisen suhtautumisen raporttien perusteella.
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            <Chip label="Vihreä = myönteinen" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 600 }} />
                            <Chip label="Oranssi = neutraali" sx={{ bgcolor: '#fff3e0', color: '#ed6c02', fontWeight: 600 }} />
                            <Chip label="Punainen = kielteinen" sx={{ bgcolor: '#ffebee', color: '#d32f2f', fontWeight: 600 }} />
                        </Stack>
                    </Stack>
                </Box>

                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} lg={4}>
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, height: '100%' }}>
                            <Typography variant="body2" color="text.secondary">Yhteistyötahoja</Typography>
                            <Typography variant="h4" sx={{ fontWeight: 700 }}>{yhteistyotahot.length}</Typography>
                            <Typography variant="body2" color="text.secondary">
                                {filteredYhteistyotahot.length} näkyvissä nykyisillä suodattimilla
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} lg={4}>
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, height: '100%' }}>
                            <Typography variant="body2" color="text.secondary">Raportteja yhteensä</Typography>
                            <Typography variant="h4" sx={{ fontWeight: 700 }}>{totalReportCount}</Typography>
                            <Typography variant="body2" color="text.secondary">
                                {visibleReportCount} raporttia näkyvissä listassa
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} lg={4}>
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, height: '100%' }}>
                            <Typography variant="body2" color="text.secondary">Aktiivisin yhteistyötaho</Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                {topYhteistyotaho?.name || 'Ei dataa'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {topYhteistyotaho ? `${topYhteistyotaho.reportCount} raporttia` : 'Tietoa ei ole saatavilla'}
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>

                <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} flexWrap="wrap">
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ flex: 1, minWidth: 280 }}>
                        <TextField
                            label="Etsi yhteistyötaho"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            variant="outlined"
                            helperText={`${filteredYhteistyotahot.length} osumaa`}
                            sx={{ flex: 1 }}
                        />
                        <TextField
                            select
                            label="Järjestä"
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            sx={{ minWidth: 220 }}
                        >
                            {SORT_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Stack>
                    <Box sx={{ minWidth: 360, width: { xs: '100%', lg: 'auto' } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
                            <Button
                                variant="contained"
                                onClick={exportToCSV}
                                disabled={Boolean(exportType)}
                                sx={{ minWidth: 170 }}
                                startIcon={exportType === 'csv' ? <CircularProgress size={16} color="inherit" /> : null}
                            >
                                {exportType === 'csv' ? 'Valmistellaan CSV...' : 'Export Raportit(.csv)'}
                            </Button>
                            <Button
                                variant="contained"
                                onClick={exportToExcel}
                                disabled={Boolean(exportType)}
                                sx={{ minWidth: 170 }}
                                startIcon={exportType === 'excel' ? <CircularProgress size={16} color="inherit" /> : null}
                            >
                                {exportType === 'excel' ? 'Valmistellaan Excel...' : 'Export Raportit(Excel)'}
                            </Button>
                        </Box>
                        {exportType && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'right' }}>
                                Käydään raportteja läpi ja valmistellaan tiedostoa...
                            </Typography>
                        )}
                    </Box>
                </Box>

                <Grid container spacing={2}>
                    {displayedYhteistyotahot?.map((yhteistyotaho) => {
                        const averageMeta = yhteistyotaho.averageMeta;

                        return (
                            <Grid key={yhteistyotaho.name} item xs={12} md={6} xl={4}>
                                <Paper
                                    variant="outlined"
                                    sx={{
                                        p: 2.25,
                                        borderRadius: 3,
                                        height: '100%',
                                        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                                        '&:hover': {
                                            transform: 'translateY(-2px)',
                                            boxShadow: 3
                                        }
                                    }}
                                >
                                    <Stack spacing={1.5} sx={{ height: '100%' }}>
                                        <Stack direction="row" justifyContent="space-between" spacing={1}>
                                            <Box>
                                                <Typography sx={{ fontFamily: 'Arial', fontWeight: 700, fontSize: '1.1rem' }}>
                                                    {yhteistyotaho.name}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {yhteistyotaho.reportCount} raporttia
                                                </Typography>
                                            </Box>
                                            <Chip
                                                label={averageMeta.label}
                                                sx={{
                                                    color: averageMeta.color,
                                                    bgcolor: averageMeta.backgroundColor,
                                                    fontWeight: 700
                                                }}
                                            />
                                        </Stack>
                                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                            <Chip label={`Viimeisin: ${yhteistyotaho.latestReportLabel}`} size="small" variant="outlined" />
                                            <Chip label={`Arvioituja raportteja: ${yhteistyotaho.arvioitujenRaporttienMaara}`} size="small" variant="outlined" />
                                        </Stack>
                                        <Box sx={{ flexGrow: 1 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                Viimeisin tapahtuma
                                            </Typography>
                                            <Typography sx={{ fontWeight: 600 }}>
                                                {yhteistyotaho.latestEventName}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {yhteistyotaho.latestLocation}
                                            </Typography>
                                        </Box>
                                        <Button
                                            variant="contained"
                                            onClick={() => handleModalOpen(yhteistyotaho.name)}
                                            sx={{ alignSelf: 'flex-start' }}
                                        >
                                            Avaa raportit
                                        </Button>
                                    </Stack>
                                </Paper>
                            </Grid>
                        );
                    })}
                </Grid>

                <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    flexDirection="column"
                    marginTop={1}
                >
                    <Box marginBottom={1}>
                        <Typography variant="body2">
                            Sivu {currentPage + 1}
                        </Typography>
                    </Box>
                    <Typography variant="body2" color="textSecondary">
                        Sivuja yhteensä: {totalPages}
                    </Typography>
                    <Box marginTop={1}>
                        <IconButton onClick={handlePreviousPage} disabled={currentPage === 0}>
                            <ArrowBackIcon />
                        </IconButton>
                        <IconButton
                            onClick={handleNextPage}
                            disabled={currentPage === totalPages - 1}
                        >
                            <ArrowForwardIcon />
                        </IconButton>
                    </Box>
                </Box>
            </Stack>
            <ReportsModal
                open={modalOpen}
                onClose={handleModalClose}
                reports={selectedYhteistyotahoData?.reports || []}
                yhteistyotaho={selectedYhteistyotaho}
                summary={selectedYhteistyotahoData}
            />
        </Paper>
    );
};

export default StatisticsPage;